import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Gift, X } from "lucide-react";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";

const SIZE = 280;
const CENTER = SIZE / 2;
const RADIUS = SIZE / 2 - 6;

function polarToXY(angleDeg, r = RADIUS) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CENTER + r * Math.sin(rad), y: CENTER - r * Math.cos(rad) };
}

function wedgePath(startAngle, endAngle) {
  const start = polarToXY(startAngle);
  const end = polarToXY(endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

const FALLBACK_COLORS = ["#FEC400", "#f97316", "#10b981", "#6366f1", "#ec4899", "#06b6d4"];

export default function WheelOfFortune() {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [spinsAvailable, setSpinsAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null); // { segment, prize }

  const load = async () => {
    try {
      const [activeRes, spinsRes] = await Promise.all([
        API.get("/wheel/active"),
        API.get("/wheel/spins-available"),
      ]);
      setCampaign(activeRes.data?.data?.campaign ?? null);
      setSpinsAvailable(spinsRes.data?.data?.total ?? 0);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo cargar la ruleta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const segments = useMemo(() => campaign?.segments ?? [], [campaign]);
  const n = segments.length;
  const anglePerSlice = n > 0 ? 360 / n : 0;

  const wedges = useMemo(() => segments.map((s, i) => {
    const start = i * anglePerSlice;
    const end = start + anglePerSlice;
    const mid = start + anglePerSlice / 2;
    const labelPos = polarToXY(mid, RADIUS * 0.62);
    const flip = mid > 90 && mid < 270;
    return {
      ...s,
      path: wedgePath(start, end),
      color: s.color || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      labelPos,
      labelRotation: mid + (flip ? 180 : 0),
    };
  }), [segments, anglePerSlice]);

  const handleSpin = async () => {
    if (spinning || spinsAvailable <= 0 || n === 0) return;
    setSpinning(true);
    try {
      const res = await API.post("/wheel/spin");
      const data = res.data?.data;
      const winnerIndex = segments.findIndex((s) => s.id === data.segment.id);
      if (winnerIndex === -1) {
        // El set de segmentos cambió entre cargar y girar — recarga y reintenta mostrar al menos el resultado.
        setResult(data);
        setSpinning(false);
        setSpinsAvailable((v) => Math.max(0, v - 1));
        return;
      }
      const theta = winnerIndex * anglePerSlice + anglePerSlice / 2;
      const current = rotation % 360;
      const neededMod = ((-theta % 360) + 360) % 360;
      let delta = neededMod - current;
      if (delta < 0) delta += 360;
      const newRotation = rotation + 5 * 360 + delta;

      setRotation(newRotation);
      setTimeout(() => {
        setResult(data);
        setSpinning(false);
        setSpinsAvailable((v) => Math.max(0, v - 1));
      }, 3200);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo girar la ruleta");
      setSpinning(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-400">Cargando ruleta...</div>;
  }

  return (
    <div className="max-w-md mx-auto px-4 py-6 pb-24">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft size={16} /> Volver
        </button>
      </div>

      {!campaign || n === 0 ? (
        <div className="text-center py-20">
          <Sparkles size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-semibold">No hay una ruleta activa en este momento</p>
          <p className="text-sm text-gray-400 mt-1">Vuelve pronto por más premios.</p>
        </div>
      ) : (
        <>
          <div className="relative mx-auto mb-6 w-full max-w-[280px] aspect-square">
            {/* Puntero */}
            <div
              className="absolute z-10"
              style={{ top: -6, left: "50%", transform: "translateX(-50%)" }}
            >
              <div style={{
                width: 0, height: 0, borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent", borderTop: "18px solid #18181b",
              }} />
            </div>

            <motion.svg
              width="100%" height="100%" viewBox={`0 0 ${SIZE} ${SIZE}`} preserveAspectRatio="xMidYMid meet"
              animate={{ rotate: rotation }}
              transition={{ duration: 3.2, ease: [0.15, 0.85, 0.3, 1] }}
              style={{ display: "block" }}
            >
              <circle cx={CENTER} cy={CENTER} r={RADIUS + 3} fill="#18181b" />
              {wedges.map((w) => (
                <g key={w.id}>
                  <path d={w.path} fill={w.color} stroke="#18181b" strokeWidth="1.5" />
                  <text
                    x={w.labelPos.x} y={w.labelPos.y}
                    fontSize="10" fontWeight="700" fill="#fff"
                    textAnchor="middle" dominantBaseline="middle"
                    transform={`rotate(${w.labelRotation}, ${w.labelPos.x}, ${w.labelPos.y})`}
                    style={{ pointerEvents: "none" }}
                  >
                    {w.label.length > 14 ? `${w.label.slice(0, 13)}…` : w.label}
                  </text>
                </g>
              ))}
              <circle cx={CENTER} cy={CENTER} r={RADIUS * 0.14} fill="#18181b" />
            </motion.svg>
          </div>

          <div className="text-center mb-4">
            <p className="text-sm text-gray-500">
              Giros disponibles: <b className="text-gray-900">{spinsAvailable}</b>
            </p>
          </div>

          <button
            onClick={handleSpin}
            disabled={spinning || spinsAvailable <= 0}
            className="w-full py-3.5 bg-supernova-500 hover:bg-supernova-600 active:scale-[0.98] text-black font-black rounded-2xl text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {spinning ? "Girando..." : spinsAvailable > 0 ? "Girar la ruleta" : "Sin giros disponibles"}
          </button>

          <button
            onClick={() => navigate("/ruleta/premios")}
            className="w-full mt-3 py-2.5 text-sm font-semibold text-gray-500 hover:text-gray-800"
          >
            Ver mis premios ganados →
          </button>
        </>
      )}

      {result && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center relative"
          >
            <button onClick={() => setResult(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700">
              <X size={18} />
            </button>
            <div className="w-16 h-16 rounded-2xl bg-supernova-100 flex items-center justify-center mx-auto mb-4">
              <Gift size={28} className="text-supernova-600" />
            </div>
            <p className="text-lg font-black text-gray-900 mb-1">¡Ganaste!</p>
            <p className="text-gray-600 mb-5">{result.segment.label}</p>
            {result.prize?.type === "coupon" && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-5">
                <p className="text-[11px] text-gray-400 mb-1">Tu código</p>
                <p className="font-mono font-black text-gray-900 tracking-wider">{result.prize.coupon.code}</p>
              </div>
            )}
            <button
              onClick={() => { setResult(null); if (result.prize?.type === "coupon") navigate("/ruleta/premios"); }}
              className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800"
            >
              {result.prize?.type === "coupon" ? "Ver mis premios" : "Cerrar"}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
